import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, User, Moon, Sun, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const { register, verifyOTP, loading: authLoading, error: authError, clearError, resendOTP } = useAuth();


  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    clearError();

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(authLoading)

    try {
      await register({
        first_name: firstName,
        last_name: lastName,
        email,
        password
      });

      setSuccess(true);
      setShowOTPModal(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

 const handleResendOTP = async () => {
  try {
    const response = await resendOTP(email, false);
    setOtpMessage('OTP resent successfully.');

    // Start 60-second cooldown
    setResendDisabled(true);
    setCountdown(60);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Hide success message after 5 seconds
    setTimeout(() => setOtpMessage(null), 5000);

  } catch (err: any) {
    setOtpError(err.message);
  }
};
  const handleOTPVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    clearError();

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setOtpError('Please enter the complete 6-digit OTP.');
      return;
    }

    try {
      await verifyOTP(email, otpString);
      setShowOTPModal(false);
      navigate('/login');
    } catch (err: any) {
      setOtpError(err.message);
    }
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  return (
    <div className={`min-h-screen w-full flex ${darkMode ? 'bg-gradient-to-br from-[#181824] to-[#232336]' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
      {/* Left: Hero Section (60%) */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden" style={{ flexBasis: '60%' }}>
        {/* Background Image */}
        <img
          src="/Login.avif"
          alt="Vela Register Visual"
          className="w-full h-full object-cover object-center absolute inset-0"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-pink-900/80" />

        {/* Logo */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-12">
          <div className="text-center">
            <img src="/Vela-Icon.png" alt="Vela Logo" className="w-80 h-80 mx-auto drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* Right: Register Form (40%) */}
      <div className={`flex flex-col justify-center items-center w-full lg:w-[40%] min-h-screen px-8 py-12 relative ${darkMode ? 'bg-[#232336]' : 'bg-white'}`}>
        <div className="w-full max-w-md mx-auto">
          {/* Back Button */}
          <div className="absolute top-6 left-6">
            <a
              href="/login"
              className={`inline-flex items-center gap-2 p-3 rounded-xl transition-all duration-200 ${darkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </a>
          </div>

          {/* Theme Toggle */}
          <div className="absolute top-6 right-6">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-3 rounded-xl transition-all duration-200 ${darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-white" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="relative inline-block">
              <img src="/Vela-Icon.png" alt="Vela Logo" className="w-20 h-20 mx-auto mb-4" />
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Welcome to <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">Vela</span>
            </h1>
          </div>

          {/* Form Header */}
          <div className="text-left mb-8">
            <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Create Account</h2>
            <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Join Vela and start collaborating with your team.</p>
          </div>

          {/* Register Form */}
          <form onSubmit={handleRegister} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              {/* First Name Field */}
              <div>
                <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>First Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    className={`w-full pl-10 pr-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                    placeholder="First name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>
              </div>

              {/* Last Name Field */}
              <div>
                <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Last Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    className={`w-full pl-10 pr-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                    placeholder="Last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                </div>
                <input
                  type="email"
                  className={`w-full pl-10 pr-4 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full pl-10 pr-12 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  placeholder="Create a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  ) : (
                    <Eye className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className={`block font-medium mb-2 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${darkMode ? 'text-white/40' : 'text-gray-400'}`} />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`w-full pl-10 pr-12 py-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white placeholder-white/50' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  ) : (
                    <Eye className={`h-5 w-5 ${darkMode ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className={`p-4 border rounded-xl ${darkMode ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>Registration successful! Redirecting to login...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <span className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Already have an account? </span>
            <a href="/login" className={`font-medium text-sm transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
              Sign in here
            </a>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md ${darkMode ? 'bg-[#232336]' : 'bg-white'} rounded-2xl shadow-2xl p-8 relative`}>
            {/* Close Button */}
            <button
              onClick={() => setShowOTPModal(false)}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            >
              <svg className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Verify Your Email</h3>
              <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                We've sent a 6-digit verification code to <span className="font-medium">{email}</span>
              </p>
            </div>

            {/* OTP Form */}
            <form onSubmit={handleOTPVerification} className="space-y-6">
              {/* OTP Input Fields */}
              <div>
                <label className={`block font-medium mb-3 text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Enter Verification Code</label>
                <div className="flex gap-3 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className={`w-12 h-12 text-center text-lg font-semibold rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 ${darkMode ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      placeholder="X"
                    />
                  ))}
                </div>
              </div>

              {/* OTP Success Message */}
              {otpMessage && (
                <div className={`p-4 border rounded-xl mb-2 ${darkMode
                    ? 'bg-green-500/10 border-green-500/20'
                    : 'bg-green-50 border-green-200'
                  }`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                        {otpMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}


              {/* OTP Error Message */}
              {otpError && (
                <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{otpError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Verify Button */}
              <button
                type="submit"
                disabled={otpLoading}
                className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {otpLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Email
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Resend OTP Link */}
            <div className="mt-6 text-center">
              <span className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>Didn't receive the code? </span>
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendDisabled}
                className={`font-medium text-sm transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                {resendDisabled
                  ? `Resend OTP in ${countdown}s`
                  : 'Resend OTP'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register; 